import { useTranslation } from "react-i18next";
import { Badge as PolarisBadge, Button as PolarisButton, DataTable } from "@shopify/polaris";
import useIsPhone from "features/files/use-is-phone";
import { DocumentSigner } from "services/work-security.service";
import SignerResendButton from "./signer-resend-button";

type Props = {
  signers: DocumentSigner[];
  companyId: string;
  roleId: string;
  documentId: string;
  /** False for a closed document or an archived role: nobody is asked to sign, so there is nothing to resend. */
  canResend: boolean;
  onDownloadSigned: (signatureId: string) => void;
  /** Called after a reminder went out, so the page reloads the role. */
  onResent: () => void;
};

const cardButtonCls =
  "inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50";

/** Who signed one role document: a table on a tablet or desktop, one card per person on a phone. */
export default function DocumentSigners({
  signers,
  companyId,
  roleId,
  documentId,
  canResend,
  onDownloadSigned,
  onResent,
}: Props) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  const signerFacts = (signer: DocumentSigner) => {
    const isSigned = signer.signatureStatus === "signed";
    return {
      statusBadge: isSigned ? (
        <PolarisBadge tone="success">{t("signed", { defaultValue: "signed" })}</PolarisBadge>
      ) : (
        <PolarisBadge tone="attention">{t("pending", { defaultValue: "pending" })}</PolarisBadge>
      ),
      signedAt:
        isSigned && signer.signedAt
          ? new Date(signer.signedAt).toLocaleDateString() + " " + new Date(signer.signedAt).toLocaleTimeString()
          : null,
      signedVersionId: signer.fileVersionId && signer.signatureId ? signer.signatureId : null,
      resendButton:
        !isSigned && !signer.collected && canResend ? (
          <SignerResendButton
            key={`${documentId}:${signer.id}`}
            companyId={companyId}
            roleId={roleId}
            documentId={documentId}
            userId={signer.id}
            onSent={onResent}
          />
        ) : null,
    };
  };

  if (isPhone) {
    return (
      <ul className="space-y-3">
        {signers.map((signer) => {
          const { statusBadge, signedAt, signedVersionId, resendButton } = signerFacts(signer);
          return (
            <li key={signer.id} className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-zinc-900 dark:text-white">{signer.name}</p>
                  {signer.position && <p className="text-xs text-zinc-500">{signer.position}</p>}
                </div>
                <div className="shrink-0">{statusBadge}</div>
              </div>
              {signedAt && <p className="mt-1 text-xs text-zinc-500">{signedAt}</p>}
              {(signedVersionId || resendButton) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {signedVersionId && (
                    <button type="button" className={cardButtonCls} onClick={() => onDownloadSigned(signedVersionId)}>
                      {t("download", { defaultValue: "Download" })}
                    </button>
                  )}
                  {resendButton}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <DataTable
      columnContentTypes={["text", "text", "text", "text", "text", "text"]}
      headings={[
        t("Name", { defaultValue: "Name" }),
        t("position", { defaultValue: "Position" }),
        t("Status", { defaultValue: "Status" }),
        t("file", { defaultValue: "File" }),
        t("Date", { defaultValue: "Date" }),
        t("actions"),
      ]}
      rows={signers.map((signer) => {
        const { statusBadge, signedAt, signedVersionId, resendButton } = signerFacts(signer);
        return [
          signer.name,
          signer.position || "-",
          statusBadge,
          signedVersionId ? (
            <PolarisButton variant="plain" onClick={() => onDownloadSigned(signedVersionId)}>
              {t("download", { defaultValue: "Download" })}
            </PolarisButton>
          ) : (
            "-"
          ),
          signedAt || "-",
          resendButton || "-",
        ];
      })}
    />
  );
}
